// =====================================================
// UNIMATCHPLAY 2026 — Scoring Engine
// Pure match play (jamkovka), scratch brutto
// Works identically for singles and foursomes
// =====================================================

import type {
  MatchPlayState,
  GameResult,
  MatchUpResult,
  TeamStanding,
  PlayerStats,
  PlayerGameStats,
  HoleScore,
  Game,
  Player,
  Team,
  MatchUp,
  CourseHole,
  TeamSide,
  WinnerSide,
  BracketPropagation,
} from '../data/types';
import { BRACKET_MAP } from '../data/types';

// =====================================================
// MATCH PLAY CALCULATION
// =====================================================

/**
 * Calculate match play state from hole scores.
 * Works for both singles and foursomes.
 *
 * @param sideAScores Map of hole_number → strokes for side A
 * @param sideBScores Map of hole_number → strokes for side B
 * @param totalHoles Total holes to play (default 18)
 * @returns MatchPlayState
 */
export function calculateMatchPlay(
  sideAScores: Map<number, number>,
  sideBScores: Map<number, number>,
  totalHoles: number = 18
): MatchPlayState {
  let aWins = 0;
  let bWins = 0;
  let holesPlayed = 0;

  // Process holes in order
  for (let hole = 1; hole <= totalHoles; hole++) {
    const aScore = sideAScores.get(hole);
    const bScore = sideBScores.get(hole);

    // Both sides must have a score for the hole to count
    if (aScore === undefined || bScore === undefined) {
      break;
    }

    holesPlayed++;

    if (aScore < bScore) {
      aWins++;
    } else if (bScore < aScore) {
      bWins++;
    }
    // Equal scores = halved hole (no points)
  }

  const holesRemaining = totalHoles - holesPlayed;
  const lead = Math.abs(aWins - bWins);
  const leaderSide: TeamSide | null = aWins > bWins ? 'a' : bWins > aWins ? 'b' : null;

  // Check if match is decided
  const isFinished = lead > holesRemaining || (holesPlayed === totalHoles);
  const isDormie = lead === holesRemaining && lead > 0 && holesRemaining > 0;

  let winnerSide: WinnerSide | null = null;
  let resultText: string | null = null;

  if (isFinished) {
    if (lead === 0) {
      winnerSide = 'halved';
      resultText = 'Halved'; // All Square after 18
    } else {
      winnerSide = leaderSide as WinnerSide;
      if (holesRemaining === 0) {
        resultText = `${lead} UP`;
      } else {
        resultText = `${lead}&${holesRemaining}`;
      }
    }
  }

  return {
    holesPlayed,
    holesRemaining,
    leaderSide,
    lead,
    isFinished,
    isDormie,
    winnerSide,
    resultText,
  };
}

/**
 * Get running match play status text for display.
 * E.g. "2 UP" (side A leads), "1 DOWN" (from side A perspective), "A/S"
 */
export function getMatchPlayStatusText(state: MatchPlayState, perspectiveSide: TeamSide = 'a'): string {
  if (state.isFinished && state.resultText) {
    return state.resultText;
  }

  if (state.lead === 0) {
    return 'A/S';
  }

  if (state.leaderSide === perspectiveSide) {
    return `${state.lead} UP`;
  } else {
    return `${state.lead} DN`;
  }
}

// =====================================================
// GAME RESULT CALCULATION
// =====================================================

/**
 * Build side score maps from hole_scores array for a specific game.
 */
export function buildScoreMaps(
  holeScores: HoleScore[]
): { sideA: Map<number, number>; sideB: Map<number, number> } {
  const sideA = new Map<number, number>();
  const sideB = new Map<number, number>();

  for (const hs of holeScores) {
    if (hs.team_side === 'a') {
      sideA.set(hs.hole_number, hs.strokes);
    } else {
      sideB.set(hs.hole_number, hs.strokes);
    }
  }

  return { sideA, sideB };
}

/**
 * Calculate game result from hole scores.
 */
export function calculateGameResult(game: Game, holeScores: HoleScore[]): GameResult {
  const gameScores = holeScores.filter(hs => hs.game_id === game.id);
  const { sideA, sideB } = buildScoreMaps(gameScores);
  const matchPlayState = calculateMatchPlay(sideA, sideB);

  let winnerSide: WinnerSide | null = null;
  let points = { a: 0, b: 0 };

  if (matchPlayState.isFinished) {
    winnerSide = matchPlayState.winnerSide;
    if (winnerSide === 'a') {
      points = { a: 1, b: 0 };
    } else if (winnerSide === 'b') {
      points = { a: 0, b: 1 };
    } else {
      points = { a: 0.5, b: 0.5 };
    }
  }

  return {
    gameId: game.id,
    type: game.type,
    matchPlayState,
    winnerSide,
    points,
  };
}

// =====================================================
// MATCH-UP RESULT CALCULATION
// =====================================================

/**
 * Calculate match-up result from its games.
 * A match-up consists of 2 singles + 1 foursome.
 */
export function calculateMatchUpResult(
  matchUp: MatchUp,
  games: Game[],
  holeScores: HoleScore[]
): MatchUpResult {
  const matchGames = games.filter(g => g.match_up_id === matchUp.id);
  const gameResults = matchGames.map(g => calculateGameResult(g, holeScores));

  let teamAPoints = 0;
  let teamBPoints = 0;
  let liveTeamAPoints = 0;
  let liveTeamBPoints = 0;

  for (const gr of gameResults) {
    // Final points (only from completed games)
    teamAPoints += gr.points.a;
    teamBPoints += gr.points.b;

    // Live points: completed games + provisional points for in-progress
    if (gr.matchPlayState.isFinished) {
      liveTeamAPoints += gr.points.a;
      liveTeamBPoints += gr.points.b;
    } else if (gr.matchPlayState.holesPlayed > 0) {
      // In-progress: leader gets provisional point, A/S = 0.5 each
      if (gr.matchPlayState.leaderSide === 'a') {
        liveTeamAPoints += 1;
      } else if (gr.matchPlayState.leaderSide === 'b') {
        liveTeamBPoints += 1;
      } else {
        // A/S — projected half point each
        liveTeamAPoints += 0.5;
        liveTeamBPoints += 0.5;
      }
    }
  }

  const allCompleted = gameResults.length === 3 && gameResults.every(gr => gr.matchPlayState.isFinished);

  let winnerTeamId: string | null = null;
  if (allCompleted) {
    if (teamAPoints > teamBPoints) {
      winnerTeamId = matchUp.team_a_id;
    } else if (teamBPoints > teamAPoints) {
      winnerTeamId = matchUp.team_b_id;
    }
    // teamAPoints === teamBPoints → draw, winnerTeamId stays null
  }

  return {
    matchUpId: matchUp.id,
    teamAPoints,
    teamBPoints,
    liveTeamAPoints,
    liveTeamBPoints,
    games: gameResults,
    winnerTeamId,
    isCompleted: allCompleted,
  };
}

// =====================================================
// BRACKET PROPAGATION
// =====================================================

/**
 * Determine which teams should be propagated to afternoon matches
 * based on semifinal results.
 */
export function propagateBracketResults(
  matchUps: MatchUp[],
  results: MatchUpResult[]
): BracketPropagation[] {
  const propagations: BracketPropagation[] = [];
  const resultMap = new Map(results.map(r => [r.matchUpId, r]));

  // Group semifinals by destination
  const destinationTeams: Record<string, { teamA?: string; teamB?: string }> = {};

  for (const matchUp of matchUps) {
    const bracketInfo = BRACKET_MAP[matchUp.round];
    if (!bracketInfo) continue; // not a semifinal

    const result = resultMap.get(matchUp.id);
    if (!result || !result.isCompleted) continue;

    // Determine winner and loser
    const winnerId = result.winnerTeamId;
    const loserId = winnerId === matchUp.team_a_id ? matchUp.team_b_id : matchUp.team_a_id;

    // Propagate winner
    if (winnerId) {
      if (!destinationTeams[bracketInfo.winnerGoesTo]) {
        destinationTeams[bracketInfo.winnerGoesTo] = {};
      }
      const dest = destinationTeams[bracketInfo.winnerGoesTo];
      if (!dest.teamA) {
        dest.teamA = winnerId;
      } else {
        dest.teamB = winnerId;
      }
    }

    // Propagate loser
    if (loserId) {
      if (!destinationTeams[bracketInfo.loserGoesTo]) {
        destinationTeams[bracketInfo.loserGoesTo] = {};
      }
      const dest = destinationTeams[bracketInfo.loserGoesTo];
      if (!dest.teamA) {
        dest.teamA = loserId;
      } else {
        dest.teamB = loserId;
      }
    }
  }

  // Convert to propagation instructions
  for (const [round, teams] of Object.entries(destinationTeams)) {
    const targetMatchUp = matchUps.find(m => m.round === round);
    if (targetMatchUp && teams.teamA && teams.teamB) {
      propagations.push({
        matchUpId: targetMatchUp.id,
        teamAId: teams.teamA,
        teamBId: teams.teamB,
      });
    }
  }

  return propagations;
}

// =====================================================
// FINAL STANDINGS
// =====================================================

/**
 * Calculate final standings (1-8) from all match-up results.
 */
export function calculateFinalStandings(
  matchUps: MatchUp[],
  results: MatchUpResult[],
  teams: Team[]
): TeamStanding[] {
  const standings: TeamStanding[] = [];
  const resultMap = new Map(results.map(r => [r.matchUpId, r]));
  const teamMap = new Map(teams.map(t => [t.id, t]));

  const placementRounds: { round: string; winnerPos: number; loserPos: number }[] = [
    { round: 'final', winnerPos: 1, loserPos: 2 },
    { round: 'place_3', winnerPos: 3, loserPos: 4 },
    { round: 'place_5', winnerPos: 5, loserPos: 6 },
    { round: 'place_7', winnerPos: 7, loserPos: 8 },
  ];

  for (const { round, winnerPos, loserPos } of placementRounds) {
    const matchUp = matchUps.find(m => m.round === round);
    if (!matchUp) continue;

    const result = resultMap.get(matchUp.id);
    const teamA = matchUp.team_a_id ? teamMap.get(matchUp.team_a_id) : null;
    const teamB = matchUp.team_b_id ? teamMap.get(matchUp.team_b_id) : null;

    if (result?.isCompleted) {
      const winnerId = result.winnerTeamId;
      const loserId = winnerId === matchUp.team_a_id ? matchUp.team_b_id : matchUp.team_a_id;
      const resultText = `${result.teamAPoints}:${result.teamBPoints}`;

      if (winnerId) {
        const winnerTeam = teamMap.get(winnerId);
        if (winnerTeam) {
          standings.push({
            position: winnerPos,
            teamId: winnerId,
            teamName: winnerTeam.name,
            teamColor: winnerTeam.color,
            finalRound: round,
            matchUpResult: resultText,
          });
        }
      }

      if (loserId) {
        const loserTeam = teamMap.get(loserId);
        if (loserTeam) {
          standings.push({
            position: loserPos,
            teamId: loserId,
            teamName: loserTeam.name,
            teamColor: loserTeam.color,
            finalRound: round,
            matchUpResult: resultText,
          });
        }
      }

      // Handle draw (both get same position)
      if (!winnerId && teamA && teamB) {
        standings.push({
          position: winnerPos,
          teamId: teamA.id,
          teamName: teamA.name,
          teamColor: teamA.color,
          finalRound: round,
          matchUpResult: resultText,
        });
        standings.push({
          position: winnerPos,
          teamId: teamB.id,
          teamName: teamB.name,
          teamColor: teamB.color,
          finalRound: round,
          matchUpResult: resultText,
        });
      }
    }
  }

  return standings.sort((a, b) => a.position - b.position);
}

// =====================================================
// PLAYER STATISTICS
// =====================================================

/**
 * Find which side a player is on in a given game.
 */
export function getPlayerSide(playerId: string, game: Game): TeamSide | null {
  if (game.type === 'single') {
    if (game.player_a_id === playerId) return 'a';
    if (game.player_b_id === playerId) return 'b';
  } else {
    if (game.foursome_a1_id === playerId || game.foursome_a2_id === playerId) return 'a';
    if (game.foursome_b1_id === playerId || game.foursome_b2_id === playerId) return 'b';
  }
  return null;
}

/**
 * Get all games a player is assigned to.
 */
export function getPlayerGames(playerId: string, games: Game[]): Game[] {
  return games.filter(g => getPlayerSide(playerId, g) !== null);
}

/**
 * Get opponent name(s) for display.
 */
function getOpponentName(game: Game, side: TeamSide, players: Map<string, Player>): string {
  const oppSide = side === 'a' ? 'b' : 'a';
  if (game.type === 'single') {
    const oppId = oppSide === 'a' ? game.player_a_id : game.player_b_id;
    return oppId ? (players.get(oppId)?.name ?? 'N/A') : 'N/A';
  } else {
    const opp1Id = oppSide === 'a' ? game.foursome_a1_id : game.foursome_b1_id;
    const opp2Id = oppSide === 'a' ? game.foursome_a2_id : game.foursome_b2_id;
    const opp1 = opp1Id ? players.get(opp1Id)?.name : '?';
    const opp2 = opp2Id ? players.get(opp2Id)?.name : '?';
    return `${opp1} + ${opp2}`;
  }
}

/**
 * Get partner name for foursome (if applicable).
 */
function getPartnerName(game: Game, playerId: string, side: TeamSide, players: Map<string, Player>): string | undefined {
  if (game.type !== 'foursome') return undefined;
  const p1Id = side === 'a' ? game.foursome_a1_id : game.foursome_b1_id;
  const p2Id = side === 'a' ? game.foursome_a2_id : game.foursome_b2_id;
  const partnerId = p1Id === playerId ? p2Id : p1Id;
  return partnerId ? players.get(partnerId)?.name : undefined;
}

/**
 * Calculate statistics for a single player across all their games.
 */
export function calculatePlayerStats(
  player: Player,
  team: Team | undefined,
  allGames: Game[],
  allHoleScores: HoleScore[],
  playerMap: Map<string, Player>,
  courseHoles: CourseHole[]
): PlayerStats {
  const playerGames = getPlayerGames(player.id, allGames);

  let totalHolesPlayed = 0;
  let birdies = 0;
  let eagles = 0;
  let holesWon = 0;
  let holesHalved = 0;
  let holesLost = 0;
  let gamesWon = 0;
  let gamesHalved = 0;
  let gamesLost = 0;
  const perGameStats: PlayerGameStats[] = [];

  const holeParMap = new Map(courseHoles.map(h => [h.number, h.par]));

  for (const game of playerGames) {
    const side = getPlayerSide(player.id, game)!;
    const gameScores = allHoleScores.filter(hs => hs.game_id === game.id);
    const { sideA, sideB } = buildScoreMaps(gameScores);
    const matchState = calculateMatchPlay(sideA, sideB);

    const mySide = side === 'a' ? sideA : sideB;
    const oppSide = side === 'a' ? sideB : sideA;

    let gameBirdies = 0;
    let gameEagles = 0;
    let gameHolesWon = 0;
    let gameHolesHalved = 0;
    let gameHolesLost = 0;
    let gameHolesPlayed = 0;

    // Process each hole
    for (let h = 1; h <= 18; h++) {
      const myScore = mySide.get(h);
      const oppScore = oppSide.get(h);
      if (myScore === undefined || oppScore === undefined) break;

      gameHolesPlayed++;
      const par = holeParMap.get(h) ?? 4;

      // Birdie / Eagle check
      if (myScore <= par - 2) {
        gameEagles++;
      } else if (myScore < par) {
        gameBirdies++;
      }

      // Hole result
      if (myScore < oppScore) {
        gameHolesWon++;
      } else if (myScore > oppScore) {
        gameHolesLost++;
      } else {
        gameHolesHalved++;
      }
    }

    // Game result from perspective of this player
    let gameWon: boolean | null = null;
    if (matchState.isFinished) {
      if (matchState.winnerSide === side) {
        gameWon = true;
        gamesWon++;
      } else if (matchState.winnerSide === 'halved') {
        gameWon = null;
        gamesHalved++;
      } else {
        gameWon = false;
        gamesLost++;
      }
    }

    totalHolesPlayed += gameHolesPlayed;
    birdies += gameBirdies;
    eagles += gameEagles;
    holesWon += gameHolesWon;
    holesHalved += gameHolesHalved;
    holesLost += gameHolesLost;

    perGameStats.push({
      gameId: game.id,
      gameType: game.type,
      opponentName: getOpponentName(game, side, playerMap),
      partnerName: getPartnerName(game, player.id, side, playerMap),
      matchResult: matchState.resultText ?? (matchState.holesPlayed > 0 ? 'Probíhá' : 'Nezačal'),
      won: gameWon,
      holesPlayed: gameHolesPlayed,
      birdies: gameBirdies,
      eagles: gameEagles,
      holesWon: gameHolesWon,
      holesHalved: gameHolesHalved,
      holesLost: gameHolesLost,
    });
  }

  const winRate = totalHolesPlayed > 0
    ? Math.round((holesWon / totalHolesPlayed) * 1000) / 10
    : 0;

  return {
    playerId: player.id,
    playerName: player.name,
    teamId: team?.id ?? '',
    teamName: team?.name ?? 'N/A',
    teamColor: team?.color ?? '#5B3A9E',
    totalHolesPlayed,
    birdies,
    eagles,
    holesWon,
    holesHalved,
    holesLost,
    winRate,
    gamesPlayed: playerGames.length,
    gamesWon,
    gamesHalved,
    gamesLost,
    perGameStats,
  };
}

/**
 * Calculate statistics for all players in the tournament.
 */
export function calculateAllPlayerStats(
  players: Player[],
  teams: Team[],
  games: Game[],
  holeScores: HoleScore[],
  courseHoles: CourseHole[]
): PlayerStats[] {
  const playerMap = new Map(players.map(p => [p.id, p]));
  const teamMap = new Map(teams.map(t => [t.id, t]));

  // Only include players who have been assigned to at least one game
  const playersWithGames = players.filter(p => getPlayerGames(p.id, games).length > 0);

  return playersWithGames.map(player =>
    calculatePlayerStats(
      player,
      player.team_id ? teamMap.get(player.team_id) : undefined,
      games,
      holeScores,
      playerMap,
      courseHoles
    )
  );
}
